<template>
  <div
    class="bg-fill-brand-secondary border-[3px] border-border-brand rounded-3xl shadow-[1px_3px_21px_0px_rgba(0,0,0,0.1)] w-[312px] px-6 py-[30px] overflow-hidden flex flex-col gap-4 text-left"
  >
    <div class="flex flex-col gap-4 w-full">
      <!-- Title (Freeman font via font-serif), clamped to 2 lines with fixed height -->
      <p
        class="text-text-primary text-xl font-serif uppercase leading-[26px] line-clamp-2 h-[52px]"
      >
        {{ offer.name }}
      </p>

      <!-- Image area (fixed height, matching Figma ~180px) -->
      <div class="relative h-[180px] flex items-center justify-center">
        <img
          v-if="offer.image_url"
          :src="offer.image_url"
          :alt="offer.name"
          class="max-w-full max-h-full object-contain rounded-2xl"
        />
        <div v-else class="w-full h-full flex items-center justify-center">
          <Icon
            icon="mdi:tag-outline"
            class="text-icon-secondary text-4xl"
            aria-hidden="true"
          />
        </div>
      </div>

      <!-- CTA area (only this is clickable) -->
      <div class="w-full">
        <button
          type="button"
          class="rounded-full h-10 w-full flex items-center justify-center px-6 py-1 gap-2 cursor-pointer transition-colors"
          :class="ctaClasses"
          :aria-label="cardAriaLabel"
          @click="onCtaClick"
        >
          <Icon
            v-if="hasClaimed"
            icon="mdi:check-circle"
            class="text-icon-primary size-4"
            aria-hidden="true"
          />
          <span
            class="text-sm leading-[20px] font-medium text-text-primary text-center"
          >
            {{ ctaLabel }}
          </span>
          <Icon
            v-if="isGated && !hasClaimed && !isSoldOut"
            icon="mdi:lock"
            class="text-icon-secondary size-4"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>

    <!-- Card footer -->
    <div class="flex items-center justify-between w-full">
      <!-- Left: gating criteria indicators (star badges, stacking) -->
      <div class="flex items-center">
        <template v-if="gatingCriterionIcons.length">
          <div
            v-for="(item, idx) in gatingCriterionIcons"
            :key="item.icon"
            class="relative size-[35px] shrink-0 flex items-center justify-center backdrop-blur-[2px] text-fill-brand"
            :class="{ '-ml-2': idx > 0, 'opacity-40': !item.qualified }"
            :style="{ zIndex: gatingCriterionIcons.length - idx }"
          >
            <!-- Star badge background (from Figma) -->
            <svg
              class="absolute inset-0 size-full"
              viewBox="0 0 31.15 29.92"
              fill="none"
            >
              <path
                d="M13.44 0.9C14.61-0.3 16.54-0.3 17.71 0.9L22.54 5.8C22.77 6.04 23.04 6.23 23.34 6.38L29.49 9.45C30.99 10.2 31.59 12.03 30.81 13.52L27.64 19.62C27.49 19.92 27.38 20.23 27.34 20.56L26.31 27.36C26.07 29.02 24.51 30.15 22.85 29.88L16.07 28.75C15.74 28.69 15.41 28.69 15.08 28.75L8.3 29.88C6.64 30.15 5.09 29.02 4.84 27.36L3.82 20.56C3.77 20.23 3.66 19.92 3.51 19.62L0.34 13.52C-0.43 12.03 0.16 10.2 1.66 9.45L7.81 6.38C8.11 6.23 8.38 6.04 8.61 5.8L13.44 0.9Z"
                fill="currentColor"
                fill-opacity="0.3"
              />
            </svg>
            <!-- Criterion icon: use image override or iconify -->
            <img
              v-if="iconImageOverrides[item.icon]"
              :src="iconImageOverrides[item.icon]"
              alt=""
              class="relative size-6 object-contain"
            />
            <Icon
              v-else
              :icon="item.icon"
              class="relative size-5"
              :class="
                item.qualified ? 'text-icon-primary' : 'text-icon-secondary'
              "
            />
          </div>
        </template>
        <!-- "Open to everyone" indicator when no gating criteria -->
        <div
          v-else
          class="relative size-[35px] shrink-0 flex items-center justify-center backdrop-blur-[2px] text-fill-brand"
        >
          <svg
            class="absolute inset-0 size-full"
            viewBox="0 0 31.15 29.92"
            fill="none"
          >
            <path
              d="M13.44 0.9C14.61-0.3 16.54-0.3 17.71 0.9L22.54 5.8C22.77 6.04 23.04 6.23 23.34 6.38L29.49 9.45C30.99 10.2 31.59 12.03 30.81 13.52L27.64 19.62C27.49 19.92 27.38 20.23 27.34 20.56L26.31 27.36C26.07 29.02 24.51 30.15 22.85 29.88L16.07 28.75C15.74 28.69 15.41 28.69 15.08 28.75L8.3 29.88C6.64 30.15 5.09 29.02 4.84 27.36L3.82 20.56C3.77 20.23 3.66 19.92 3.51 19.62L0.34 13.52C-0.43 12.03 0.16 10.2 1.66 9.45L7.81 6.38C8.11 6.23 8.38 6.04 8.61 5.8L13.44 0.9Z"
              fill="currentColor"
              fill-opacity="0.3"
            />
          </svg>
          <Icon
            icon="mdi:earth"
            class="relative size-5 text-icon-primary"
            aria-hidden="true"
          />
        </div>
      </div>

      <!-- Right: info tooltip + admin dropdown -->
      <div class="flex items-center gap-2">
        <InfoTooltip side="bottom">
          <template #trigger>
            <button
              type="button"
              aria-label="Offer details"
              class="size-11 bg-fill-brand-secondary backdrop-blur-[17.5px] rounded-full flex items-center justify-center cursor-pointer"
            >
              <Icon
                icon="material-symbols:info-outline"
                class="text-icon-primary size-5"
                aria-hidden="true"
              />
            </button>
          </template>
          <p>{{ tooltipLabel }}</p>
        </InfoTooltip>
        <OfferItemDropdown
          v-if="channelStore.permissions.manageOffers"
          :offer-id="offer.id"
          @deleted="emit('deleted')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";
import type { ResultOf } from "@graphql-typed-document-node/core";

export const OfferCardFragment = gql(/* GraphQL */ `
  fragment OfferCardData on Offer {
    id
    name
    description
    short_description
    image_url
    user_has_claimed
    authorization_rule_group {
      id
      ...AuthorizationRuleGroupForGating
    }
    poolStats {
      available
    }
  }
`);

export type OfferCardData = ResultOf<typeof OfferCardFragment>;
</script>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue/dist/iconify.js";
import {
  useProvideGatingPermissions,
  type AuthorizationRuleForGatingType,
  type AuthorizationRuleGroupForGatingType,
} from "@/features/gating/use-gating-permissions";
import { useIsGated } from "@/features/gating/evaluation/use-is-gated";
import { useGatingForAction } from "@/features/gating/evaluation/use-gating-for-action";
import { useGatingEvaluator } from "@/features/gating/evaluation/use-gating-evaluator";
import { ChannelPermission } from "@f3/shared/permissions";
import { ExperienceAction, ExperienceType } from "@/enums/experience-type.enum";
import { useChannelStore } from "@/stores/channel.store";
import type { GatingStrategyType } from "@/features/gating/strategies/gating-strategy";
import OfferItemDropdown from "./offer-item-dropdown.vue";
import InfoTooltip from "@/components/ui/tooltip/info-tooltip.vue";

const props = defineProps<{
  offer: OfferCardData;
}>();

const emit = defineEmits<{
  openDetail: [];
  deleted: [];
}>();

const { t } = useI18n();
const channelStore = useChannelStore();

useProvideGatingPermissions(
  computed(
    () =>
      props.offer
        .authorization_rule_group as AuthorizationRuleGroupForGatingType,
  ),
);

const gatingEvaluator = useGatingEvaluator();

const isGated = useIsGated(
  () => props.offer.authorization_rule_group,
  ChannelPermission.claimOffers,
);

// Gating: wrap the CTA action so the auth modal shows when user doesn't qualify
const gatingExperience = computed(() => ({
  experienceId: props.offer.id,
  experienceTitle: props.offer.name,
  experienceType: ExperienceType.Offer,
  experienceAction: ExperienceAction.CLAIM_OFFER,
}));

const { handler: onCtaClick } = useGatingForAction(
  ChannelPermission.claimOffers,
  () => emit("openDetail"),
  { gatingExperience },
);

const isSoldOut = computed(
  () => props.offer.poolStats != null && props.offer.poolStats.available === 0,
);

const hasClaimed = computed(() => props.offer.user_has_claimed);

const ctaLabel = computed(() => {
  if (hasClaimed.value) return t("channel.offers.card.cta.claimed");
  if (isSoldOut.value) return t("channel.offers.card.cta.soldOut");
  return t("channel.offers.card.cta.claimNow");
});

const ctaClasses = computed(() => {
  if (hasClaimed.value) return "bg-fill-brand";
  if (isSoldOut.value) return "bg-fill-disabled";
  return "bg-fill-brand-secondary";
});

const cardAriaLabel = computed(() => {
  if (hasClaimed.value) {
    return `${props.offer.name} — Already Claimed`;
  }
  if (isSoldOut.value) {
    return `${props.offer.name} — Sold Out`;
  }
  if (isGated.value) {
    return `${props.offer.name} — Members only`;
  }
  return props.offer.name;
});

const tooltipLabel = computed(() => {
  return (
    props.offer.short_description || props.offer.description || props.offer.name
  );
});

const gatingCriterionIcons = computed(() => {
  const group = props.offer
    .authorization_rule_group as ResultOf<AuthorizationRuleGroupForGatingType>;

  const rules = (group?.authorization_rules ??
    []) as ResultOf<AuthorizationRuleForGatingType>[];
  if (!rules?.length) return [];

  const gatingTypes = rules.reduce(
    (acc, rule) => {
      const types = gatingEvaluator.getGatingTypesForRule(rule);
      const matches = gatingEvaluator.matchRule(rule);

      const gatingTypeMatches = types.reduce(
        (typeAcc, type) => {
          return { ...typeAcc, [type]: (acc[type] ?? true) && matches };
        },
        {} as Record<GatingStrategyType, boolean>,
      );

      return { ...acc, ...gatingTypeMatches };
    },
    {} as Record<GatingStrategyType, boolean>,
  );

  return (Object.entries(gatingTypes) as [GatingStrategyType, boolean][])
    .map(([type, qualified]) => {
      const metadata = gatingEvaluator.getMetadataForGatingType(type);
      return { icon: metadata.icon, qualified };
    })
    .filter((item) => !!item);
});

/** Map criterion icons to Figma raster assets (no iconify match). */
const iconImageOverrides: Record<string, string> = {
  "material-symbols:token-outline": "/gating-wallet.png",
};
</script>
